import {
	noop,
	useIntl,
	type UserBasicRequirement,
	type UserRequirement,
	type UserUKselfCategorisationUserProvidedValues
} from '@capsicum/common';
import { useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';

import { uniqueRequirementAtom, useSubmitRequirementStateByIdentifierQuery } from '../../../data-access';

export enum InvestorTypeOptions {
	HIGH_NET_WORTH = 'high_net_worth',
	RESTRICTED = 'restricted',
	SOPHISTICATED = 'certified_sophisticated'
}

export type UkSelfCategorisationFormProps = {
	onFormValidChange: (isFormValid: boolean) => void;
	onFormValuesChange: (payload: UserUKselfCategorisationUserProvidedValues) => void;
	requirement: UserBasicRequirement;
};

type UseUkSelfCategorisationProps = {
	isUniqueRequirement?: boolean;
	requirement: UserRequirement;
};

export const useUkSelfCategorisation = ({ isUniqueRequirement, requirement }: UseUkSelfCategorisationProps) => {
	const { isLoading, mutate } = useSubmitRequirementStateByIdentifierQuery();
	const { $t } = useIntl();

	const [index, setIndex] = useState(0);
	const [showModal, setShowModal] = useState(!isUniqueRequirement);
	const [selectedInvestorType, setSelectedInvestorType] = useState<null | string>(null);
	const [formValues, setformValues] = useState<null | UserUKselfCategorisationUserProvidedValues>(null);
	const [isFormValid, setIsFormValid] = useState(false);

	const setShowRequirementPage = useSetAtom(uniqueRequirementAtom);

	const investorOptions = [
		{
			enabled: true,
			id: InvestorTypeOptions.RESTRICTED,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.investorType1')
		},
		{
			enabled: false,
			id: InvestorTypeOptions.SOPHISTICATED,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.investorType2')
		},
		{
			enabled: true,
			id: InvestorTypeOptions.HIGH_NET_WORTH,
			label: $t('jurisdictionRequirements.ukSelfCategorisation.investorType3')
		}
	].filter(({ enabled }) => Boolean(enabled));

	const hiddenSecondButton = useMemo(() => {
		return index !== 1 || selectedInvestorType !== InvestorTypeOptions.RESTRICTED;
	}, [index, selectedInvestorType]);

	const disabledFirstButton = useMemo(() => {
		switch (index) {
			case 0:
				return !selectedInvestorType;
			case 1:
				return selectedInvestorType !== InvestorTypeOptions.RESTRICTED && !isFormValid;
			case 2:
				return !isFormValid;
		}
	}, [index, isFormValid, selectedInvestorType]);

	const title = useMemo(() => {
		if (index === 0) return $t('jurisdictionRequirements.ukSelfCategorisation.statementTitle');

		switch (selectedInvestorType) {
			case InvestorTypeOptions.RESTRICTED:
				return $t('jurisdictionRequirements.ukSelfCategorisation.restrictedInvestorTitlePage');
			case InvestorTypeOptions.SOPHISTICATED:
				return $t('jurisdictionRequirements.ukSelfCategorisation.sophisticatedInvestorTitlePage');
			case InvestorTypeOptions.HIGH_NET_WORTH:
				return $t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthInvestorTitlePage');
		}
	}, [index, selectedInvestorType]);

	const confirmButton = useMemo(() => {
		if (index === 0 || (index <= 1 && selectedInvestorType === InvestorTypeOptions.RESTRICTED)) {
			return $t('global.button.continue');
		}

		return $t('jurisdictionRequirements.ukSelfCategorisation.confirmStatement');
	}, [index, selectedInvestorType]);

	const confirmationModalDescription = useMemo(() => {
		switch (selectedInvestorType) {
			case InvestorTypeOptions.RESTRICTED:
				return $t('jurisdictionRequirements.ukSelfCategorisation.restristedInvestorConfirmationModalText');
			case InvestorTypeOptions.HIGH_NET_WORTH:
				return $t('jurisdictionRequirements.ukSelfCategorisation.highNetWorthInvestorConfirmationModalText');
			default:
				return '';
		}
	}, [selectedInvestorType]);

	const shouldShowConfirmationModal = useMemo(() => {
		if (selectedInvestorType === InvestorTypeOptions.RESTRICTED) {
			return (
				formValues?.restrictedHasInvestedLessThanTenPercent &&
				formValues?.restrictedWillInvestLessThanTenPercent
			);
		}

		if (selectedInvestorType === InvestorTypeOptions.HIGH_NET_WORTH) {
			return (
				!formValues?.highDoesNotApply && (formValues?.highAnnualIncomeChoice || formValues?.highNetAssetsChoice)
			);
		}
	}, [selectedInvestorType, formValues]);

	const onModalConfirmPress = () => {
		setShowModal(false);
	};

	const handleFormValidChange = (isRestrictedFormValid: boolean) => {
		setIsFormValid(isRestrictedFormValid);
	};

	const handleFormValuesChange = (values: UserUKselfCategorisationUserProvidedValues) => {
		setformValues(values);
	};

	const submitRequirement = (data: UserUKselfCategorisationUserProvidedValues, onSuccess = noop) => {
		const payload: UserUKselfCategorisationUserProvidedValues = {
			category: data.category,
			firstName: data.firstName,
			highActualAnnualIncome: data.highActualAnnualIncome,
			highActualNetAssets: data.highActualNetAssets,
			highAnnualIncomeChoice: data.highAnnualIncomeChoice,
			highNetAssetsChoice: data.highNetAssetsChoice,
			lastName: data.lastName,
			restrictedHasInvestedLessThanTenPercent: data.restrictedHasInvestedLessThanTenPercent,
			restrictedHasInvestedPercent: data.restrictedHasInvestedPercent,
			restrictedWillInvestLessThanTenPercent: data.restrictedWillInvestLessThanTenPercent,
			restrictedWillInvestPercent: data.restrictedWillInvestPercent,
			sophisticatedCertifiedDocument: data.sophisticatedCertifiedDocument,
			sophisticatedFirmName: data.sophisticatedFirmName,
			sophisticatedHasCertificate: data.sophisticatedHasCertificate
		};

		mutate({ requirementData: payload, requirementIdentifier: requirement.identifier }, { onSuccess });
	};

	const onSubmitSteps = (showConfirmationModal?: () => void) => {
		if (!formValues) return;
		if (shouldShowConfirmationModal && showConfirmationModal) {
			showConfirmationModal();

			return;
		}
		submitRequirement(formValues, () => {
			if (isUniqueRequirement) setShowRequirementPage(false);
		});
	};

	return {
		confirmationModalDescription,
		confirmButton,
		disabledFirstButton,
		handleFormValidChange,
		handleFormValuesChange,
		hiddenSecondButton,
		investorOptions,
		isSubmitLoading: isLoading,
		onModalConfirmPress,
		onSubmitSteps,
		selectedInvestorType,
		setIndex,
		setSelectedInvestorType,
		showModal,
		title
	};
};
